/*
Pre-persist image pipeline: decode → resize → JPEG re-encode.

Two reasons we run this on every captured photo before persisting:
  1. **Privacy** — iOS / Android cameras stamp the JPEG with EXIF
     including GPS, device make/model, software version. Drawing the
     decoded bitmap to a fresh canvas drops every EXIF tag (canvas
     export starts from a blank metadata slate).
  2. **Quota** — a raw 12 MP iPhone JPEG is ~3-5 MB; HEIC even more
     after the implicit transcode `<input type="file">` performs.
     Re-encoding at long-edge 1600 px / quality 0.85 brings the
     typical capture down to ~300-700 KB, so 1 GB of IDB photos
     ≈ 1500-3000 captures instead of ~250.

Mirrors the Android `compressCapture` in CaptureLauncher.kt (which uses
BitmapFactory.Options.inSampleSize to downscale at the decoder).

Two-path strategy:
  • **Primary (Vanadium / Chromium)** — `createImageBitmap(blob, {
    resizeWidth, resizeHeight, resizeQuality, imageOrientation })`.
    The browser downscales at the decoder in native memory — the JS
    heap never sees a full-resolution RGBA buffer. Equivalent of
    Android's `inSampleSize`. Encoding goes through OffscreenCanvas
    where available, falling back to a DOM canvas otherwise. This
    path resolved the "Unable to complete previous operation due to
    low memory" Chromium OOM that fired during capture on Vanadium
    (the legacy `<img>` + full-canvas pipeline held the original
    File, an Image-decoded bitmap, and a 1600×1200 RGBA canvas — ~17
    MB peak — simultaneously during `canvasToBlob()`).
  • **Fallback (iOS Safari)** — `<img>` + DOM canvas. iOS Safari has
    been observed to silently fail `createImageBitmap(blob)` for HEIC
    inputs in standalone PWA mode. The `<img>` pipeline handles HEIC
    natively and auto-applies EXIF orientation when rendered. We
    aggressively release references (img.src='', canvas.width=0) so
    the GC reclaims the full-resolution buffers between captures.
*/

const kTargetLongEdgePx: number = 1200  // assumed divergence from Android's 1600: Vanadium WebContent process crashed on 1600 due to peak RGBA allocation. Backup ZIP import accepts any size, so cross-platform read still works.
const kJpegQuality: number = 0.85       // parité Android (JPEG_QUALITY = 85)

/*
Decode the input blob, downscale so the longest side fits within
kTargetLongEdgePx (no upscale — small inputs pass through at native
size), and re-encode as JPEG. Returns the new blob; the original
input is left untouched.

Fails safely: any decode / canvas / encode error rejects with a
descriptive message. The capture flow surfaces these as a generic
"erreur de traitement" toast rather than corrupting the IDB store.
*/
export async function reencodeForStorage(inBlob: Blob): Promise<Blob>
{
	// Primary path: native-decoder resize via createImageBitmap. Skipped
	// entirely on platforms missing the API; caught and demoted to the
	// fallback if it errors at runtime (iOS Safari HEIC quirk).
	if (typeof createImageBitmap === 'function')
	{
		try
		{
			return await reencodeViaImageBitmap(inBlob)
		}
		catch (vErr)
		{
			console.warn('createImageBitmap path failed, falling back to <img>+canvas', vErr)
			// fall through to the universal <img>+canvas path
		}
	}
	return reencodeViaImgElement(inBlob)
}

/*
Probe source dimensions via a throwaway <img>, then re-decode the blob
through createImageBitmap with resize options so the browser produces
only the downscaled bitmap. The probe <img> is released immediately
(src='') so its decoded buffer is unreferenced before the resized
bitmap is allocated.
*/
async function reencodeViaImageBitmap(inBlob: Blob): Promise<Blob>
{
	// Prefer the binary JPEG header parser: zero decode, ~64 KB read,
	// dimensions ready in microseconds. The legacy <img> probe is kept
	// as fallback for HEIC and other non-JPEG inputs (iOS Safari).
	// On Vanadium the picker always delivers JPEG so the parser hits
	// every time — eliminating the ~50 MB peak from a full <img>
	// decode that was the residual OOM cause after iteration 1.
	const vJpegDims = await readJpegDimensions(inBlob)
	const vDims = vJpegDims ?? await probeDimensions(inBlob)
	const vSrcW = vDims.width
	const vSrcH = vDims.height
	if (vSrcW <= 0 || vSrcH <= 0) throw new Error('Image has zero dimensions')

	const { dstW: vDstW, dstH: vDstH } = scaleTarget(vSrcW, vSrcH)

	let vBitmap: ImageBitmap | null = null
	try
	{
		vBitmap = await createImageBitmap(inBlob, {
			resizeWidth: vDstW,
			resizeHeight: vDstH,
			resizeQuality: 'high',
			imageOrientation: 'from-image',  // honour EXIF orientation; <img> pipeline does this for free
		})
		if (vBitmap.width <= 0 || vBitmap.height <= 0) throw new Error('createImageBitmap returned empty')
		const vOut = await encodeViaCanvas(vBitmap, vDstW, vDstH)
		if (!vOut) throw new Error('Canvas encode returned null')
		return vOut
	}
	finally
	{
		try { vBitmap?.close() } catch { /* .close() unavailable on older runtimes; GC will reclaim */ }
	}
}

/*
Legacy universal path. Decodes via <img> (which handles HEIC + EXIF
orientation natively on iOS Safari), draws into a DOM canvas at the
target size, encodes via toBlob. Aggressively dereferences the img
and zeroes the canvas so the GC can reclaim the buffers between
captures.
*/
async function reencodeViaImgElement(inBlob: Blob): Promise<Blob>
{
	const vUrl = URL.createObjectURL(inBlob)
	let vImg: HTMLImageElement | null = null
	const vCanvas = document.createElement('canvas')
	try
	{
		vImg = await loadImage(vUrl)
		const vSrcW = vImg.naturalWidth
		const vSrcH = vImg.naturalHeight
		if (vSrcW <= 0 || vSrcH <= 0) throw new Error('Image has zero dimensions')

		const { dstW: vDstW, dstH: vDstH } = scaleTarget(vSrcW, vSrcH)
		vCanvas.width = vDstW
		vCanvas.height = vDstH
		const vCtx = vCanvas.getContext('2d')
		if (!vCtx) throw new Error('2D context unavailable')
		vCtx.drawImage(vImg, 0, 0, vDstW, vDstH)
		const vOut = await canvasToBlob(vCanvas, 'image/jpeg', kJpegQuality)
		if (!vOut) throw new Error('Canvas toBlob returned null')
		return vOut
	}
	finally
	{
		if (vImg) vImg.src = ''  // release the decoded bitmap reference
		URL.revokeObjectURL(vUrl)
		vCanvas.width = 0
		vCanvas.height = 0  // hint to the GC: drop the RGBA buffer asap
	}
}

/*
Binary JPEG header parser. Reads the first 64 KB of the blob and
walks the marker chain until it hits a Start-Of-Frame (SOF) marker,
which carries the image dimensions at a fixed offset. No decode, no
canvas, no <img> — peak memory stays under the 64 KB buffer.

Returns null when the blob is not a recognisable JPEG (HEIC, PNG,
etc.) so the caller can fall through to the universal <img> probe.

Marker layout (JPEG / ISO 10918-1):
- SOI:        FF D8                    (start-of-image, 2 bytes)
- segment:    FF xx [LL LH] payload    (length includes the length bytes)
- SOF0–SOFn:  FF C0..CF                (exclusive of C4 DHT, C8 JPG, CC DAC)
  payload bytes: [precision][HH HL][WW WL][...]
  → height at offset +5 from marker, width at +7
- standalone markers (no length): D0–D7 (RST), 01 (TEM)
*/
async function readJpegDimensions(inBlob: Blob): Promise<{ width: number; height: number } | null>
{
	try
	{
		const vHead = await inBlob.slice(0, 65_536).arrayBuffer()  // SOF markers typically sit in the first few KB
		const vView = new DataView(vHead)
		if (vView.byteLength < 4 || vView.getUint16(0) !== 0xFFD8) return null  // not a JPEG (no SOI)

		let vI = 2
		while (vI < vView.byteLength - 9)
		{
			// Skip fill bytes 0xFF that some encoders emit between segments
			while (vI < vView.byteLength && vView.getUint8(vI) === 0xFF) vI++
			if (vI >= vView.byteLength) return null
			const vMarker = vView.getUint8(vI)
			vI++  // consume the marker byte; the FF prefix(es) were consumed above

			// Standalone markers carry no length field — skip them whole
			if (vMarker === 0x00 || vMarker === 0x01 || (vMarker >= 0xD0 && vMarker <= 0xD7)) continue
			if (vI + 2 > vView.byteLength) return null
			const vSegLength = vView.getUint16(vI)
			if (vSegLength < 2) return null  // malformed

			// SOF range, exclude entropy/arithmetic tables (C4 DHT, CC DAC) and JPG (C8)
			const vIsSof = vMarker >= 0xC0 && vMarker <= 0xCF && vMarker !== 0xC4 && vMarker !== 0xC8 && vMarker !== 0xCC
			if (vIsSof)
			{
				if (vI + 7 > vView.byteLength) return null
				const vHeight = vView.getUint16(vI + 3)  // skip length(2) + precision(1) = +3 from segment payload start
				const vWidth = vView.getUint16(vI + 5)
				if (vWidth <= 0 || vHeight <= 0) return null
				return { width: vWidth, height: vHeight }
			}

			vI += vSegLength
		}
		return null
	}
	catch
	{
		return null
	}
}

/*
Lightweight dimension probe via <img>. The decoded bitmap is released
(src='' + revokeObjectURL) before the caller proceeds to a heavier
decode pass — so the peak memory of the probe and the subsequent
resize-decode don't overlap.
*/
async function probeDimensions(inBlob: Blob): Promise<{ width: number; height: number }>
{
	const vUrl = URL.createObjectURL(inBlob)
	let vImg: HTMLImageElement | null = null
	try
	{
		vImg = await loadImage(vUrl)
		return { width: vImg.naturalWidth, height: vImg.naturalHeight }
	}
	finally
	{
		if (vImg) vImg.src = ''
		URL.revokeObjectURL(vUrl)
	}
}

/*
Compute the destination size that fits inside kTargetLongEdgePx while
preserving aspect ratio. Inputs smaller than the target pass through
unchanged.
*/
function scaleTarget(inSrcW: number, inSrcH: number): { dstW: number; dstH: number }
{
	const vLong = Math.max(inSrcW, inSrcH)
	const vScale = vLong > kTargetLongEdgePx ? kTargetLongEdgePx / vLong : 1
	return {
		dstW: Math.max(1, Math.round(inSrcW * vScale)),
		dstH: Math.max(1, Math.round(inSrcH * vScale)),
	}
}

/*
Encode a decoded ImageBitmap into a JPEG Blob. Uses OffscreenCanvas
where supported (Vanadium / modern Chromium) — bitmap.close() runs
immediately after drawImage so the native bitmap memory is reclaimed
before convertToBlob's async encoding pass. Falls back to a DOM
canvas otherwise.
*/
async function encodeViaCanvas(inBitmap: ImageBitmap, inW: number, inH: number): Promise<Blob | null>
{
	// OffscreenCanvas + convertToBlob path DISABLED. Suspect the
	// worker-bound canvas was holding a duplicate RGBA buffer in the
	// render process — Vanadium kept crashing on capture (full tab
	// reload) even with createImageBitmap resize. Falls through to the
	// DOM canvas path, which is reliable on every tested device. Only
	// revisit if the DOM path ever becomes a measured bottleneck.
	//
	// if (typeof OffscreenCanvas !== 'undefined' && 'convertToBlob' in OffscreenCanvas.prototype)
	// {
	//     const vOff = new OffscreenCanvas(inW, inH)
	//     const vCtx = vOff.getContext('2d', { alpha: false }) as OffscreenCanvasRenderingContext2D | null
	//     if (!vCtx) throw new Error('2D context unavailable')
	//     vCtx.drawImage(inBitmap, 0, 0)
	//     return await vOff.convertToBlob({ type: 'image/jpeg', quality: kJpegQuality })
	// }
	const vCanvas = document.createElement('canvas')
	try
	{
		vCanvas.width = inW
		vCanvas.height = inH
		const vCtx = vCanvas.getContext('2d')
		if (!vCtx) throw new Error('2D context unavailable')
		vCtx.drawImage(inBitmap, 0, 0)
		return await canvasToBlob(vCanvas, 'image/jpeg', kJpegQuality)
	}
	finally
	{
		vCanvas.width = 0
		vCanvas.height = 0
	}
}

/*
`<img>` based decoder. Resolves once the native pipeline has fully
loaded the bitmap (works for JPEG, PNG, HEIC since iOS 14, WebP).
*/
function loadImage(inUrl: string): Promise<HTMLImageElement>
{
	return new Promise<HTMLImageElement>((inResolve, inReject) => {
		const vImg = new Image()  // ephemeral decoder, never inserted into the DOM
		vImg.onload = () => inResolve(vImg)
		vImg.onerror = () => inReject(new Error('Image decode failed'))
		vImg.src = inUrl
	})
}

/*
Promise wrapper around HTMLCanvasElement.toBlob — the API itself is
callback-based. iOS Safari resolves it on the next microtask.
*/
function canvasToBlob(inCanvas: HTMLCanvasElement, inMime: string, inQuality: number): Promise<Blob | null>
{
	return new Promise<Blob | null>((inResolve) => {
		inCanvas.toBlob((inBlob) => inResolve(inBlob), inMime, inQuality)
	})
}
